<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import Footer from '@/Components/Footer.vue';
import Header from '../Components/Header.vue';
import { usePage } from '@inertiajs/vue3';
import CookieConsent from '@/Components/CookieConsent.vue';
import CompareWidget from '@/Components/CompareWidget.vue';
import { useCompareStore } from '@/store/compareModule';

// Get shared props
const { canLogin, canRegister } = usePage().props;
const { auth } = usePage().props;

// Initialize compare store
const compareStore = useCompareStore();

// Get the current page info for route detection
const page = usePage();

// Function to check if we're on the compare page
const checkIfComparePage = () => {
  const currentUrl = page.url;
  const isComparePage = currentUrl.includes('/compare');
  compareStore.setComparePage(isComparePage);
};

// Detect screen size with proper lifecycle management
const isLargeScreen = ref(false);
const updateScreenSize = () => {
  isLargeScreen.value = window.innerWidth >= 1024;
};

onMounted(() => {
  // Load compare tools from storage
  compareStore.loadFromStorage();
  
  // Check if we're on the compare page
  checkIfComparePage();
  
  // Set initial screen size
  updateScreenSize();
  
  // Add event listener for responsive design
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  // Clean up event listener to prevent memory leaks
  window.removeEventListener('resize', updateScreenSize);
});

// Watch for route changes to update compare page status
watch(() => page.url, () => {
  checkIfComparePage();
});
</script>

<template>
  <!-- Add skip link for keyboard users (hidden unless focused) -->
  <a 
    href="#main-content" 
    class="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md"
  >
    Skip to main content
  </a>
  
  <!-- Mobile Design - Keeping your original structure -->
  <div v-if="!isLargeScreen" class="w-full bg-gray-800">
    <Header :canRegister="canRegister" />
    <main id="main-content" class="flex-grow w-full p-4">
      <slot></slot>
    </main>
    <Footer />
  </div>
  
  <!-- Large Screen Design - Keeping your original structure -->
  <div v-else class="min-h-screen bg-gray-900 text-white flex items-center justify-center py-12">
    <div class="container mx-auto p-4 bg-gray-800 rounded-lg shadow-lg" style="min-height: 90vh">
      <Header :canRegister="canRegister" />
      <main id="main-content" class="flex-grow container mx-auto p-4 pt-0">
        <slot></slot>
      </main>
      <Footer />
    </div>
  </div>
  
  <!-- Include Cookie Consent Banner -->
  <CookieConsent />
  
  <!-- Compare Widget -->
  <CompareWidget />
</template>

<style>
a {
  text-decoration: none;
}
/* Skip link styling - hidden unless focused */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
.focus\:not-sr-only:focus {
  position: static;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: normal;
}
/* Improve dark mode accessibility */
:root {
  color-scheme: dark;
}
</style>